import React, { useRef, useState, useEffect } from 'react';

import i18n from 'i18n-js';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Formik, Field, FormikProps, useFormikContext } from 'formik';
import * as FormikFields from 'formik-material-ui';
import {
    Avatar,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField as MuiTextField,
    FormControlLabel,
    TableHead,
    Box,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import DeleteIcon from '@material-ui/icons/Delete';
import * as yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { parsePhoneNumber } from 'react-phone-number-input';
import ObjectID from 'bson-objectid';
import { Switch } from 'formik-material-ui';

import { RestaurantUserCreateModel, UserData } from 'store/usersStore/reducer';
import { ApplicationState } from 'store';
import { default as restaurantStore } from 'store/restaurantDetailsStore';
import { regexHelper } from 'helpers/regexHelper';
import { Roles, OrderServices } from 'constants/enums';
import agent from 'api/agent';
import { getCoordinatesFromTheAddress, trimLeftAndRight } from 'helpers/helperFunctions';

import QR from '../../restaurantInformation/qr';
import Finance from '../../restaurantInformation/finance';
import { AddRestaurantUserForm } from '../../users/components';
import {
    CustomPhoneInput,
    Input,
    PopupConfirmation,
    SectionTab,
    Text,
    UserRoleLabel,
    ImageContainer,
    FormSkeleton,
} from 'components/shared';
import globalStore from 'store/globalStore';
import AddNewUserModal from './components/AddNewUserModal';
import RestaurantUsers from './components/RestaurantsUsers';
import CreateRestaurantHeader from './components/CreateRestaurantHeader';
import UsersTable from './components/UsersTable';
import { useStyles } from './styles';
import PaymentProviders from './components/PaymentProviders';
import { RestaurantPaymentSettings } from 'store/restaurantsStore/reducer';
import AdminAdvancedSettings from './components/AdvancedSettings';
import { useFirstLoad } from 'hooks/useFirstLoad';

export interface RestaurantContactPerson {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
}

export interface RestaurantCreateForm {
    name: string;
    addressLine: string;
    zip: string;
    city: string;
    phoneNumber: string;
    email: string;
    website: string;
    logoUrl: string;
    contactPersons: RestaurantContactPerson[];
}

export interface RestaurantCreateData {
    name: string;
    addressLine: string;
    zip: string;
    city: string;
    phoneNumber: string;
    email: string;
    website: string;
    logoUrl: string;
    latitude: number;
    longitude: number;
    percentageForDineIn?: number;
    percentageForTakeAway: number;
    percentageForHomeDelivery: number;
    type: any[];
    timeZone: string;
    isCoolBox: boolean;
    isPublic: boolean;
    coolBoxName?: string;
    isDeliverAnytime: boolean;
    reservationModule: {
        isEnabled: boolean;
    };
}

function FormStatusObserver({ onChange }: { onChange: (dirty: boolean) => void }) {
    const { dirty } = useFormikContext();
    useEffect(() => onChange(dirty), [dirty, onChange]);
    return null;
}

export default function CreateEditRestaurantPage() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const cuisineTypes = useSelector(
        (state: ApplicationState) => state.restaurantDetails.cuisineTypes
    );
    const params = useParams<{ id: string }>();
    const [restaurantId, setRestaurantId] = useState<string>(params?.id || '');
    const [isDetailsLoading, settleDetails] = useFirstLoad(ObjectID.isValid(params.id));
    const [selectedContactPerson, setSelectedContactPerson] = useState<UserData | string>('');
    const [paymentProviders, setPaymentProviders] =
        useState<RestaurantPaymentSettings | undefined>(undefined);
    const [previuosState, setPreviousState] = useState<RestaurantCreateData>({
        name: '',
        addressLine: '',
        zip: '',
        city: '',
        phoneNumber: '',
        email: '',
        website: '',
        logoUrl: '',
        latitude: 0,
        longitude: 0,
        type: [],
        timeZone: '',
        percentageForDineIn: 1,
        percentageForTakeAway: 1,
        percentageForHomeDelivery: 1,
        isCoolBox: false,
        isPublic: false,
        coolBoxName: '',
        isDeliverAnytime: false,
        reservationModule: { isEnabled: false },
    });
    const [restaurantName, setRestaurantName] = useState<string>('');
    const [pendingRestaurantValues, setPendingRestaurantValues] =
        useState<RestaurantCreateData | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const saveInProgress = useRef(false);

    function handleDownloadQRName(name: string, service: OrderServices) {
        switch (service) {
            case OrderServices.HomeDelivery:
                setDownloadHomeDeliveryQrName(name);
                break;
            case OrderServices.DineIn:
                setDownloadDineInQrName(name);
                break;
            default:
                setDownloadTakeAwayQrName(name);
        }
    }
    useEffect(() => {
        if (ObjectID.isValid(params.id)) {
            getRestaurantDetails().finally(settleDetails);
        } else if (!!params.id) {
            history.goBack();
        }

        if (!cuisineTypes.length) {
            dispatch(restaurantStore.actionCreators.getCuisineTypes());
        }
        return () => {
            dispatch({
                type: 'GET_QR_CODES_SUCCESS',
                data: [],
            });
        };
    }, [params.id]); // eslint-disable-line

    function getRestaurantDetails() {
        return agent.Restaurants.GetDetails(params.id).then((response) => {
            updatePreviousState(response);
            setFormValues({
                name: response.name,
                addressLine: response.addressLine,
                zip: response.zip,
                city: response.city,
                phoneNumber: response.phoneNumber,
                email: response.email,
                logoUrl: response.logoUrl,
                website: response.website,
                latitude: response.latitude,
                longitude: response.longitude,
                type: response.type,
                percentageForDineIn: response.percentageForDineIn.toFixed(2) || '0.00',
                percentageForTakeAway: response.percentageForTakeAway.toFixed(2) || '0.00',
                percentageForHomeDelivery: response.percentageForHomeDelivery.toFixed(2) || '0.00',
                timeZone: response.timeZone,
                isCoolBox: response.coolBoxData?.isCoolBox,
                isPublic: response.coolBoxData?.isPublic,
                coolBoxName: response.coolBoxData?.coolBoxName,
                isDeliverAnytime: response.isDeliverAnytime || false,
                reservationModule: {
                    isEnabled: response.reservationModule?.isEnabled ?? false,
                },
            });
            setPaymentProviders({
                posPaymentProvider: response.paymentDetails.posPaymentProvider,
                webPaymentProvider: response.paymentDetails.webPaymentProvider,
            });
            setLogoToUpload(response.logoUrl ?? '');
            setContactPersons(response.contactPersons);
            setPreviuosContactPersons(response.contactPersons);
            setDownloadTakeAwayQrName(response.qrCodesData.takeAwayQrCodeName);
            setDownloadHomeDeliveryQrName(response.qrCodesData.homeDeliveryQrCodeName);
            setDownloadDineInQrName(response.qrCodesData.dineInQrCodeName);
            if (window?.location?.origin?.includes('localhost')) {
                setRestaurantName(`localhost-${response.name}`);
            } else if (window?.location?.origin?.includes('dev')) {
                setRestaurantName(`dev-${response.name}`);
            } else if (window?.location?.origin?.includes('stage')) {
                setRestaurantName(`stage-${response.name}`);
            } else {
                setRestaurantName(response.name);
            }
        });
    }

    function updatePreviousState(response: any) {
        setPreviousState({
            name: response.name,
            addressLine: response.addressLine,
            zip: response.zip,
            city: response.city,
            phoneNumber: response.phoneNumber,
            email: response.email,
            logoUrl: response.logoUrl,
            website: response.website,
            latitude: response.latitude,
            longitude: response.longitude,
            type: response.type,
            percentageForDineIn: response.percentageForDineIn || 0,
            percentageForTakeAway: response.percentageForTakeAway || 0,
            percentageForHomeDelivery: response.percentageForHomeDelivery || 0,
            timeZone: response.timeZone,
            isCoolBox: response.isCoolBox,
            isPublic: response.isPublic,
            coolBoxName: response.coolBoxName,
            isDeliverAnytime: response.isDeliverAnytime || false,
            reservationModule: {
                isEnabled: response.reservationModule?.isEnabled ?? false,
            },
        });
    }

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .max(
                128,
                i18n.t('form.errors.maxLength', {
                    name: 'Name',
                    length: 128,
                })
            )
            .required(
                i18n.t('form.errors.required', {
                    name: 'Name',
                })
            ),
        addressLine: yup.string().required(
            i18n.t('form.errors.required', {
                name: 'Address line',
            })
        ),
        email: yup
            .string()
            .required(
                i18n.t('form.errors.required', {
                    name: 'Email',
                })
            )
            .email(i18n.t('form.errors.email')),
        phoneNumber: yup
            .string()
            .required(
                i18n.t('form.errors.required', {
                    name: 'Phone number',
                })
            )
            .matches(regexHelper.phoneNumber, i18n.t('form.errors.phoneNumber')),
        zip: yup.string().max(10),
        city: yup.string().matches(regexHelper.city, i18n.t('form.errors.city')),
        coolBoxName: yup.string().when('isCoolBox', {
            is: true,
            then: yup.string().required(
                i18n.t('form.errors.required', {
                    name: 'CoolBox Name',
                })
            ),
        }),
        percentageForDineIn: yup.number().min(0).max(99).required(),
        percentageForTakeAway: yup.number().min(0).max(99).required(),
        percentageForHomeDelivery: yup.number().min(0).max(99).required(),
    });
    const [showAddContactModal, setShowAddContactModal] = useState(false);
    const history = useHistory();

    function handlePercentageInput(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value;
        //only two digits after comma allowed
        const match = raw.match(/^\d{0,2}(\.\d{0,2})?$/);

        if (match) {
            formRef.current?.setFieldValue(e.target.name, raw);
        }
    }

    function createContactPerson(model: RestaurantUserCreateModel) {
        dispatch(globalStore.actionCreators.showSpiner());
        agent.Users.CreateRestaurantOwner(model)
            .then((response) => setContactPersons([...contactPersons, response]))
            .catch((e) => dispatch(globalStore.actionCreators.showToaster('error', e)))
            .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
    }

    const createContactPersons = (model: RestaurantContactPerson[], id: string, name: string) => {
        return new Promise((resolve, reject) => {
            const persons = model.map(
                (p) =>
                    ({
                        ...p,
                        restaurantId: id,
                        isContactPerson: true,
                        restaurantName: name,
                    } as RestaurantUserCreateModel)
            );
            try {
                persons.forEach((x) => {
                    agent.Users.CreateRestaurantOwner(x);
                });
                return resolve('Created succesfully!');
            } catch (ex) {
                return reject(ex);
            }
        });
    };
    const removeContactPerson = (user: UserData) => {
        dispatch(globalStore.actionCreators.showSpiner());
        agent.Users.EditRestaurantUser({
            restaurant: {
                id: params.id,
                name: formValues.name,
                addressLine: formValues.addressLine,
                zip: formValues.zip,
                city: formValues.city,
                logoUrl: formValues.logoUrl ?? '',
            },
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phoneNumber: user.phoneNumber,
            isContactPerson: false,
            positions: user.restaurants.find((x) => x.restaurantId === params.id)!.positions,
            isOwner: user.roles[0].name === Roles.OWNER,
            isPayInAdvance: false,
        })
            .then((response) => {
                setContactPersons(contactPersons.filter((x) => x.id !== response.id));
            })
            .catch((e) => dispatch(globalStore.actionCreators.showToaster('error', e)))
            .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
    };
    const fileUploadRef: any = React.useRef(null);

    const [formValues, setFormValues] = useState({
        name: '',
        addressLine: '',
        zip: '',
        city: '',
        phoneNumber: '',
        email: '',
        website: '',
        logoUrl: '',
        latitude: 0,
        longitude: 0,
        percentageForDineIn: '0.00',
        percentageForTakeAway: '0.00',
        percentageForHomeDelivery: '0.00',
        type: [],
        timeZone: '',
        isCoolBox: false,
        isPublic: false,
        coolBoxName: '',
        isDeliverAnytime: false,
        reservationModule: { isEnabled: false },
    });
    const [contactPersons, setContactPersons] = useState<UserData[]>([]);
    const [previuosContactPersons, setPreviuosContactPersons] = useState<UserData[]>([]);
    const [logoToUpload, setLogoToUpload] = useState<Blob | null>(null);
    const [logoHasChanged, setLogoHasChanged] = useState(false);
    const [formChanged, setFormChanged] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [showUsersTable, setShowUsersTable] = useState(false);
    const [downloadDineInQrName, setDownloadDineInQrName] = useState<string>('');
    const [downloadTakeAwayQrName, setDownloadTakeAwayQrName] = useState<string>('');
    const [downloadHomeDeliveryQrName, setDownloadHomeDeliveryQrName] = useState<string>('');

    const browserData = useSelector((state: ApplicationState) => state.user.browserData);

    async function lastSubmit(values: RestaurantCreateData) {
        if (saveInProgress.current) {
            return;
        }
        saveInProgress.current = true;
        setIsSaving(true);
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            if (
                !params.id ||
                values.latitude === 0 ||
                values.longitude === 0 ||
                values.addressLine !== previuosState.addressLine ||
                values.city !== previuosState.city ||
                values.zip !== previuosState.zip
            ) {
                const { latitude, longitude, timeZone } = await getCoordinatesFromTheAddress(
                    `${values.addressLine}, ${values.zip} ${values.city}`
                );
                values.latitude = latitude;
                values.longitude = longitude;
                values.timeZone = timeZone;
            }
            if (values.logoUrl && logoHasChanged) {
                values.logoUrl = await fileUploadRef.current?.uploadImage();
            }

            const cuisineTypes: string[] = [];
            values.type.map((type) => cuisineTypes.push(type.id));
            const newValues = { ...values };
            newValues.type = cuisineTypes;
            newValues.reservationModule = {
                isEnabled: values.isCoolBox ? false : values.reservationModule.isEnabled,
            };
            if (params.id) {
                await agent.Restaurants.Update(params.id, newValues).then((response) => {
                    if (contactPersons.length > previuosContactPersons.length) {
                        let newContactPersons = [...contactPersons];
                        newContactPersons.splice(0, previuosContactPersons.length);
                        createContactPersons(newContactPersons, response.id, response.name);
                    }
                    setLogoHasChanged(false);
                    setFormChanged(false);
                    updatePreviousState(response);
                    setFormValues({
                        name: response.name,
                        addressLine: response.addressLine,
                        zip: response.zip,
                        city: response.city,
                        phoneNumber: response.phoneNumber,
                        email: response.email,
                        logoUrl: response.logoUrl,
                        website: response.website,
                        latitude: response.latitude,
                        longitude: response.longitude,
                        type: response.type,
                        percentageForDineIn: (response.percentageForDineIn || 0).toFixed(2),
                        percentageForTakeAway: (response.percentageForTakeAway || 0).toFixed(2),
                        percentageForHomeDelivery: (
                            response.percentageForHomeDelivery || 0
                        ).toFixed(2),
                        timeZone: response.timeZone,
                        isCoolBox: response.coolBoxData?.isCoolBox,
                        isPublic: response.coolBoxData?.isPublic,
                        coolBoxName: response.coolBoxData?.coolBoxName,
                        isDeliverAnytime: response.isDeliverAnytime || false,
                        reservationModule: {
                            isEnabled: response.reservationModule?.isEnabled ?? false,
                        },
                    });
                    dispatch(
                        globalStore.actionCreators.showToaster(
                            'success',
                            i18n.t('messages.updatedSuccesfully')
                        )
                    );
                    // getRestaurantDetails();
                });
            } else {
                await agent.Restaurants.Create(newValues)
                    /* .then((response) => {
                    // @ts-ignore
                    return createContactPersons(contactPersons, response.id, response.name);
                }) */
                    .then((response) => {
                        //history.goBack();
                        setRestaurantId(response.id);
                        setActiveTab(1);
                        //history.push(`/restaurants/${response.id}`);
                    });
            }
        } catch (e) {
            dispatch(globalStore.actionCreators.showToaster('error', e));
        } finally {
            saveInProgress.current = false;
            setIsSaving(false);
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    const formRef = useRef<FormikProps<typeof formValues>>(null);
    const handleSubmit = () => {
        if (formRef.current && !saveInProgress.current && !formRef.current.isSubmitting) {
            formRef.current.handleSubmit();
        }
    };

    const renderTab = () => {
        switch (activeTab) {
            case 0:
                return (
                    <>
                        <Formik
                            innerRef={formRef}
                            enableReinitialize={true}
                            initialValues={formValues}
                            validationSchema={validationSchema}
                            onSubmit={async (values, { setFieldValue }) => {
                                const parsedPhoneNumber = parsePhoneNumber(values.phoneNumber);
                                if (parsedPhoneNumber) {
                                    if (!parsedPhoneNumber.country) {
                                        const fixedPhoneNumber = values.phoneNumber.replace(
                                            '+' + parsedPhoneNumber.countryCallingCode.toString(),
                                            browserData?.country_calling_code
                                        );
                                        setFieldValue('phoneNumber', fixedPhoneNumber);
                                        values.phoneNumber = fixedPhoneNumber;
                                    }
                                }
                                const valuesToSave: RestaurantCreateData = {
                                    ...values,
                                    percentageForDineIn: parseFloat(values.percentageForDineIn),
                                    percentageForTakeAway: parseFloat(values.percentageForTakeAway),
                                    percentageForHomeDelivery: parseFloat(
                                        values.percentageForHomeDelivery
                                    ),
                                    name: trimLeftAndRight(values.name),
                                    addressLine: trimLeftAndRight(values.addressLine),
                                    city: trimLeftAndRight(values.city),
                                    website: trimLeftAndRight(values.website),
                                };
                                if (
                                    params.id &&
                                    previuosState.reservationModule.isEnabled &&
                                    (values.isCoolBox || !values.reservationModule.isEnabled)
                                ) {
                                    setPendingRestaurantValues(valuesToSave);
                                    return;
                                }
                                await lastSubmit(valuesToSave);
                            }}
                        >
                            {({ setFieldValue, values, errors }) => (
                                <>
                                    <FormStatusObserver onChange={setFormChanged} />
                                    <Form className={classes.formInnerContainer}>
                                        <div className={classes.leftContainer}>
                                            <h6 className={classes.sectionTitle}>
                                                {i18n
                                                    .t('restaurant.restaurantInformation')
                                                    .toUpperCase()}
                                            </h6>
                                            <div className={classes.flexRowSpaceBetween}>
                                                <div style={{ width: '49%' }}>
                                                    {' '}
                                                    <Input
                                                        id="name"
                                                        name="name"
                                                        type="text"
                                                        error={errors.name}
                                                        variant="filled"
                                                        placeholder={i18n.t('form.restaurantName')}
                                                        label={i18n.t('form.restaurantName')}
                                                        className={classes.inputContainer}
                                                    />
                                                    <CustomPhoneInput
                                                        containerStyle={{
                                                            margin: '0',
                                                            height: '35px',
                                                            marginBottom: 30,
                                                        }}
                                                        invalid={errors.phoneNumber}
                                                        placeholder={i18n.t('form.phoneNumber')}
                                                        value={values.phoneNumber}
                                                        onChange={(val: any) =>
                                                            setFieldValue('phoneNumber', val)
                                                        }
                                                    />{' '}
                                                    <Input
                                                        id="email"
                                                        placeholder={i18n.t('form.email')}
                                                        name="email"
                                                        error={errors.email}
                                                        type="text"
                                                        label={i18n.t('form.email')}
                                                        variant="filled"
                                                        className={classes.inputContainer}
                                                    />
                                                </div>
                                                <div style={{ width: '49%' }}>
                                                    <Input
                                                        id="addressLine"
                                                        placeholder={i18n.t(
                                                            'form.restaurantAddress'
                                                        )}
                                                        name="addressLine"
                                                        error={errors.addressLine}
                                                        type="text"
                                                        label={i18n.t('form.restaurantAddress')}
                                                        variant="filled"
                                                        className={classes.inputContainer}
                                                    />
                                                    <div className={classes.halfFormInputContainer}>
                                                        <Input
                                                            id="zip"
                                                            className={classes.halfFormInput}
                                                            placeholder={i18n.t('form.zip')}
                                                            name="zip"
                                                            error={errors.zip}
                                                            type="text"
                                                            label={i18n.t('form.zip')}
                                                            variant="filled"
                                                        />
                                                        <Input
                                                            id="city"
                                                            className={classes.halfFormInput}
                                                            placeholder={i18n.t('form.city')}
                                                            name="city"
                                                            error={errors.city}
                                                            type="text"
                                                            label={i18n.t('form.city')}
                                                            variant="filled"
                                                        />
                                                    </div>
                                                    <Input
                                                        id="website"
                                                        placeholder={i18n.t('form.webpage')}
                                                        name="website"
                                                        error={errors.website}
                                                        type="text"
                                                        label={i18n.t('form.webpage')}
                                                        variant="filled"
                                                        className={classes.inputContainer}
                                                    />
                                                </div>
                                            </div>
                                            <h6
                                                className={classes.sectionTitle}
                                                style={{ marginTop: 0 }}
                                            >
                                                {i18n.t('restaurant.percentageFor').toUpperCase()}
                                            </h6>
                                            <Input
                                                placeholder={i18n.t('form.dineIn')}
                                                name="percentageForDineIn"
                                                error={errors.percentageForDineIn}
                                                type="number"
                                                label={i18n.t('form.dineIn')}
                                                variant="filled"
                                                className={classes.percentageInput}
                                                maxLength={2}
                                                value={values.percentageForDineIn}
                                                onChange={handlePercentageInput}
                                                inputMode="decimal"
                                                inputProps={{
                                                    min: 0,
                                                    max: 99,
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                            className={classes.percentageString}
                                                            style={{
                                                                display:
                                                                    values.percentageForDineIn?.toString()
                                                                        .length
                                                                        ? 'flex'
                                                                        : 'none',
                                                            }}
                                                        >
                                                            %
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <Input
                                                placeholder={i18n.t('form.takeAway')}
                                                name="percentageForTakeAway"
                                                error={errors.percentageForTakeAway}
                                                type="number"
                                                inputMode="decimal"
                                                onChange={handlePercentageInput}
                                                label={i18n.t('form.takeAway')}
                                                variant="filled"
                                                className={classes.percentageInput}
                                                maxLength={2}
                                                inputProps={{
                                                    min: 0,
                                                    max: 99,
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                            className={classes.percentageString}
                                                            style={{
                                                                display:
                                                                    values.percentageForTakeAway.toString()
                                                                        .length
                                                                        ? 'flex'
                                                                        : 'none',
                                                            }}
                                                        >
                                                            %
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <Input
                                                placeholder={i18n.t('form.homeDelivery')}
                                                name="percentageForHomeDelivery"
                                                error={errors.percentageForHomeDelivery}
                                                type="number"
                                                inputMode="decimal"
                                                onChange={handlePercentageInput}
                                                label={i18n.t('form.homeDelivery')}
                                                variant="filled"
                                                className={classes.percentageInput}
                                                maxLength={2}
                                                inputProps={{
                                                    min: 0,
                                                    max: 99,
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                            className={classes.percentageString}
                                                            style={{
                                                                display:
                                                                    values.percentageForHomeDelivery.toString()
                                                                        .length
                                                                        ? 'flex'
                                                                        : 'none',
                                                            }}
                                                        >
                                                            %
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <h6
                                                className={classes.sectionTitle}
                                                style={{
                                                    marginTop: 0,
                                                    marginBottom: 12,
                                                }}
                                            >
                                                {i18n.t('restaurant.contactPerson').toUpperCase()}
                                            </h6>
                                        </div>
                                        <div className={classes.rightContainer}>
                                            <div className={classes.uploadLogoContainer}>
                                                <ImageContainer
                                                    ref={fileUploadRef}
                                                    photo={values.logoUrl}
                                                    onChange={(_, fileUrl) => {
                                                        setFieldValue('logoUrl', fileUrl);
                                                        setLogoHasChanged(true);
                                                    }}
                                                    styles={{ height: '100%', width: '100%' }}
                                                />
                                            </div>
                                            <h6
                                                className={classes.sectionTitle}
                                                style={{ marginTop: 5, marginBottom: 15 }}
                                            >
                                                {i18n.t('restaurant.typeOfCuisine').toUpperCase()}
                                            </h6>
                                            <Autocomplete
                                                options={cuisineTypes}
                                                getOptionLabel={(option) => option.name}
                                                getOptionSelected={(option, value) =>
                                                    option.id === value.id
                                                }
                                                classes={{
                                                    option: classes.dropdownItem,
                                                    listbox: classes.listBox,
                                                    paper: classes.paper,
                                                }}
                                                renderOption={(option) => {
                                                    const exists = !!values.type.find(
                                                        (currentOption: any) =>
                                                            currentOption.id === option.id
                                                    );
                                                    return (
                                                        <div
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                let newValues;
                                                                if (exists) {
                                                                    newValues = values.type.filter(
                                                                        (selectedOption: any) =>
                                                                            selectedOption.id !==
                                                                            option.id
                                                                    );
                                                                } else {
                                                                    newValues = [
                                                                        ...values.type,
                                                                        option,
                                                                    ];
                                                                }
                                                                setFieldValue('type', newValues);
                                                            }}
                                                            className={`${classes.width100} ${
                                                                classes.bottomLine
                                                            } ${
                                                                exists
                                                                    ? classes.selectedDropdownItem
                                                                    : ''
                                                            }`}
                                                        >
                                                            <Checkbox
                                                                color="primary"
                                                                checked={exists}
                                                            />
                                                            {option.name}
                                                        </div>
                                                    );
                                                }}
                                                renderInput={(params) => (
                                                    <>
                                                        <MuiTextField
                                                            {...params}
                                                            name="type"
                                                            label={
                                                                values.type.length === 0
                                                                    ? i18n.t('common.chooseTheType')
                                                                    : `${
                                                                          values.type.length
                                                                      } ${i18n.t(
                                                                          'restaurant.cuisines'
                                                                      )}`
                                                            }
                                                            variant="filled"
                                                            fullWidth
                                                        />
                                                    </>
                                                )}
                                            />
                                            <Input
                                                component={FormikFields.CheckboxWithLabel}
                                                variant="filled"
                                                checked={values.isDeliverAnytime}
                                                name={'isDeliverAnytime'}
                                                key="isDeliverAnytime"
                                                type="checkbox"
                                                Label={{
                                                    label: i18n.t('restaurant.deliverAnytime'),
                                                }}
                                                placeholder={i18n.t('restaurant.deliverAnytime')}
                                                color="primary"
                                            />
                                            <h6
                                                className={classes.sectionTitle}
                                                style={{ marginTop: 10, marginBottom: 15 }}
                                            >
                                                {i18n.t('restaurant.jamesCoolBox').toUpperCase()}
                                            </h6>
                                            <div className={classes.coolBoxContainer}>
                                                <Input
                                                    component={FormikFields.CheckboxWithLabel}
                                                    variant="filled"
                                                    name="isCoolBox"
                                                    type="checkbox"
                                                    Label={{
                                                        label: i18n.t('restaurant.jamesCoolBox'),
                                                    }}
                                                    placeholder={i18n.t('restaurant.jamesCoolBox')}
                                                    color="primary"
                                                />
                                                <br />
                                                <FormControlLabel
                                                    aria-label="Acknowledge"
                                                    style={{ fontSize: 14 }}
                                                    control={
                                                        <Field
                                                            component={Switch}
                                                            name="isPublic"
                                                            color="primary"
                                                            type="checkbox"
                                                            disabled={!values.isCoolBox}
                                                            style={{ fontSize: 14 }}
                                                        />
                                                    }
                                                    label={i18n.t('restaurant.publicCoolBox')}
                                                />
                                                <br />
                                                <Input
                                                    id="coolbox-name"
                                                    placeholder={i18n.t('restaurant.coolBoxName')}
                                                    name="coolBoxName"
                                                    error={errors.coolBoxName}
                                                    type="text"
                                                    label={i18n.t('restaurant.coolBoxName')}
                                                    variant="filled"
                                                    className={classes.inputContainer}
                                                    disabled={!values.isCoolBox}
                                                    style={{ marginTop: 10 }}
                                                />
                                            </div>
                                            {!values.isCoolBox && (
                                                <div className={classes.reservationContainer}>
                                                    <div className={classes.reservationSection}>
                                                        <div className={classes.reservationHeader}>
                                                            <h6
                                                                className={classes.sectionTitle}
                                                                style={{
                                                                    marginTop: 0,
                                                                    marginBottom: 12,
                                                                }}
                                                            >
                                                                {i18n
                                                                    .t('restaurant.reservations')
                                                                    .toUpperCase()}
                                                            </h6>
                                                            <span
                                                                className={classes.reservationBadge}
                                                            >
                                                                {i18n
                                                                    .t('restaurant.paidAddOn')
                                                                    .toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    checked={
                                                                        values.reservationModule
                                                                            ?.isEnabled ?? false
                                                                    }
                                                                    disabled={isSaving}
                                                                    onChange={(_, checked) =>
                                                                        setFieldValue(
                                                                            'reservationModule.isEnabled',
                                                                            checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label={i18n.t(
                                                                'restaurant.reservationModule'
                                                            )}
                                                        />
                                                        <div
                                                            className={
                                                                classes.reservationDescription
                                                            }
                                                        >
                                                            {i18n.t(
                                                                'restaurant.reservationModuleDescription'
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Form>
                                    <PopupConfirmation
                                        open={pendingRestaurantValues !== null}
                                        close={() => setPendingRestaurantValues(null)}
                                        title={i18n.t('restaurant.confirmDisableReservationModule')}
                                        description={i18n.t(
                                            'restaurant.confirmDisableReservationModuleDescription'
                                        )}
                                        activeBtn={i18n.t('restaurant.disableReservationModule')}
                                        action={() => {
                                            if (pendingRestaurantValues !== null) {
                                                lastSubmit(pendingRestaurantValues);
                                            }
                                        }}
                                    />
                                    <TableContainer>
                                        <Table stickyHeader aria-label="simple table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell colSpan={6} padding="none">
                                                        <SectionTab
                                                            disabled={!params.id}
                                                            text={i18n.t(
                                                                'restaurant.addContactPerson'
                                                            )}
                                                            onClick={(
                                                                event: React.MouseEvent<HTMLButtonElement>
                                                            ) => {
                                                                /* if (
                                                            !!!params.id &&
                                                            contactPersons.length < 2
                                                        ) {
                                                            setShowAddContactModal(true);
                                                        } else {
                                                            setAnchorEl(event.currentTarget);
                                                        } */
                                                                setAnchorEl(event.currentTarget);
                                                            }}
                                                        />
                                                        <AddNewUserModal
                                                            anchorEl={anchorEl}
                                                            handleClose={() => setAnchorEl(null)}
                                                            openNew={() =>
                                                                setShowAddContactModal(true)
                                                            }
                                                            openExists={() =>
                                                                setShowUsersTable(true)
                                                            }
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {contactPersons.map((person: UserData) => (
                                                    <TableRow key={person.id}>
                                                        <TableCell scope="row">
                                                            <List>
                                                                <ListItem alignItems="center">
                                                                    <ListItemAvatar>
                                                                        <Avatar
                                                                            alt="Remy Sharp"
                                                                            src="/static/images/avatar/1.jpg"
                                                                        />
                                                                    </ListItemAvatar>
                                                                    <Text
                                                                        text={`${person.firstName} ${person.lastName}`}
                                                                        maxLength={20}
                                                                    />
                                                                </ListItem>
                                                            </List>
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            <Text
                                                                text={person.email}
                                                                fontSize={14}
                                                                maxLength={30}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {person.phoneNumber}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {person.restaurants
                                                                .find(
                                                                    (x) =>
                                                                        x.restaurantId === params.id
                                                                )
                                                                ?.positions.join(', ') || '-'}
                                                        </TableCell>
                                                        <TableCell
                                                            align="left"
                                                            style={{ width: 140 }}
                                                        >
                                                            <UserRoleLabel
                                                                role={person.roles[0].name}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                aria-label="more"
                                                                aria-controls="long-menu"
                                                                aria-haspopup="true"
                                                                onClick={() => {
                                                                    setSelectedContactPerson(
                                                                        person
                                                                    );
                                                                }}
                                                            >
                                                                <DeleteIcon color={'primary'} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}

                                                <PopupConfirmation
                                                    open={
                                                        Object.keys(selectedContactPerson).length >
                                                        0
                                                    }
                                                    close={() => setSelectedContactPerson('')}
                                                    title={i18n.t('common.deleteUser')}
                                                    description={i18n.t('confirmation.deleteUser')}
                                                    activeBtn={i18n.t('common.delete')}
                                                    action={() =>
                                                        typeof selectedContactPerson === 'object' &&
                                                        removeContactPerson(selectedContactPerson)
                                                    }
                                                />
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </Formik>
                    </>
                );
            case 1:
                return (
                    <PaymentProviders
                        restaurantId={restaurantId}
                        paymentProviders={paymentProviders}
                        restaurantName={restaurantName}
                    />
                );
            case 2:
                return <RestaurantUsers />;
            case 3:
                return (
                    <Finance
                        restaurantId={params.id}
                        isOld={true}
                        style={{ position: 'relative' }}
                    />
                );
            case 4:
                return (
                    <QR
                        restaurantId={params.id}
                        isAdmin={true}
                        downloadDineInQrName={downloadDineInQrName}
                        downloadTakeAwayQrName={downloadTakeAwayQrName}
                        downloadHomeDeliveryQrName={downloadHomeDeliveryQrName}
                        setDownloadQrName={handleDownloadQRName}
                    />
                );
            case 5:
                return <AdminAdvancedSettings restaurantId={params.id} />;

            default:
                return <></>;
        }
    };

    function getRestaurantDetailsTabs() {
        /**
         * If there is restaurant to view then we show related tabs
         */
        let tabs = [];
        if (!!restaurantId) {
            tabs.push('Payment');
        }
        if (!!params.id) {
            tabs = tabs.concat([
                i18n.t('restaurant.restaurantUsers'),
                i18n.t('restaurant.finance'),
                i18n.t('restaurant.qr'),
                i18n.t('common.advancedSettings'),
            ]);
        }
        return tabs;
    }

    return (
        <>
            <div className={classes.formContainer}>
                <CreateRestaurantHeader
                    tabs={[
                        !!params.id
                            ? i18n.t('restaurant.restaurantDetails')
                            : i18n.t('restaurant.newRestaurant').toUpperCase(),
                        ...getRestaurantDetailsTabs(),
                    ]}
                    toUpdate={!!params.id}
                    onAddClick={() => handleSubmit()}
                    onBackClick={() => {
                        history.push('/restaurants');
                    }}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    formChanged={formChanged}
                    isSaving={isSaving}
                />
                {renderTab()}
                <FormSkeleton
                    isLoading={isDetailsLoading}
                    columns={[7]}
                    aside={3}
                    style={{ top: 55 }}
                />
            </div>
            <Dialog open={showAddContactModal} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">
                    {i18n.t('user.createNewUser')}
                    <div className={classes.dialogTitle}></div>
                </DialogTitle>
                <DialogContent>
                    <AddRestaurantUserForm
                        createUser={createContactPerson}
                        restaurants={[
                            {
                                id: params.id,
                                name: formValues.name,
                                addressLine: formValues.addressLine,
                                zip: formValues.zip,
                                city: formValues.city,
                                logoUrl: formValues.logoUrl ?? '',
                            },
                        ]}
                        currentRestaurant={{
                            id: params.id,
                            name: formValues.name,
                            addressLine: formValues.addressLine,
                            zip: formValues.zip,
                            city: formValues.city,
                            logoUrl: formValues.logoUrl ?? '',
                        }}
                        cancel={() => setShowAddContactModal(false)}
                        isContactPerson={true}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={showUsersTable} maxWidth={'lg'} className={classes.usersDialog}>
                <UsersTable
                    close={() => setShowUsersTable(false)}
                    isContactPersons={true}
                    restaurant={{
                        id: params.id,
                        name: formValues.name,
                    }}
                />
            </Dialog>
            {![0, 1, 3].includes(activeTab) && (
                <Box className={classes.restaurantName}>
                    <Box component="span">{restaurantName}</Box>
                </Box>
            )}
        </>
    );
}
